import { useRouter } from "next/router";
import MetaTags from "./MetaTags";
import DemoSignupForm from "./demoSignupModal";
import Footer from "./footer";
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Stack,
  VStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { FaArrowRight } from "react-icons/fa";
import {
  getServiceSchema,
  getFAQSchema,
  getBreadcrumbSchema,
  siteConfig,
} from "../config/seo";

/**
 * Additive SEO landing page template.
 * Does not change existing product/auth flows — only links to existing Demo/Trial routes.
 */
export default function SeoLandingPage({ page }) {
  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("gray.800", "white");
  const bodyStrong = useColorModeValue("gray.700", "gray.200");
  const heroBg = useColorModeValue(
    "linear(to-br, #f7fafc, #edf2f7)",
    "linear(to-br, gray.900, gray.800)"
  );
  const brand = "#3A56D4";

  const structuredData = [
    getServiceSchema({
      serviceType: page.serviceType,
      description: page.description,
    }),
    getFAQSchema(page.faqs),
    getBreadcrumbSchema([
      { name: "Home", url: `${siteConfig.url}/` },
      { name: page.breadcrumbName, url: `${siteConfig.url}${page.path}` },
    ]),
  ].filter(Boolean);

  return (
    <>
      <MetaTags
        title={page.title}
        description={page.description}
        keywords={page.keywords}
        url={`${siteConfig.url}${page.path}`}
        image={siteConfig.defaultImage}
        structuredData={structuredData}
      />

      <Box bg={bg} minH="100vh">
        {/* Hero */}
        <Box
          bgGradient={heroBg}
          borderBottom="1px solid"
          borderColor={borderColor}
          py={{ base: 12, md: 16 }}
          px={4}
        >
          <Container maxW="900px" textAlign="center">
            <Text
              fontSize="sm"
              fontWeight="700"
              letterSpacing="0.12em"
              textTransform="uppercase"
              color={brand}
              mb={3}
            >
              {page.eyebrow}
            </Text>
            <Heading
              as="h1"
              fontSize={{ base: "2xl", md: "4xl" }}
              color={headingColor}
              mb={4}
              lineHeight="1.25"
            >
              {page.h1}
            </Heading>
            <Text fontSize={{ base: "md", md: "lg" }} color={muted} maxW="720px" mx="auto" mb={8}>
              {page.intro}
            </Text>
            <Stack
              direction={{ base: "column", sm: "row" }}
              spacing={4}
              justify="center"
            >
              <Button
                onClick={onOpen}
                size="lg"
                bg={brand}
                color="white"
                px={8}
                borderRadius="full"
                fontWeight="600"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: "#2A46B0" }}
              >
                Request Demo
              </Button>
              <Button
                onClick={() => router.push("/directTrialpage")}
                size="lg"
                variant="outline"
                borderColor={brand}
                color={brand}
                px={8}
                borderRadius="full"
                fontWeight="600"
                _hover={{ bg: brand, color: "white" }}
              >
                Start Free Trial
              </Button>
            </Stack>
          </Container>
        </Box>

        <Container maxW="900px" py={{ base: 10, md: 14 }} px={4}>
          <VStack spacing={12} align="stretch">
            {page.sections.map((section) => (
              <Box
                key={section.heading}
                as="section"
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="xl"
                p={{ base: 6, md: 8 }}
              >
                <Heading
                  as="h2"
                  fontSize={{ base: "xl", md: "2xl" }}
                  color={headingColor}
                  mb={4}
                >
                  {section.heading}
                </Heading>
                {section.paragraphs?.map((p, idx) => (
                  <Text key={idx} color={muted} mb={3} lineHeight="1.7">
                    {p}
                  </Text>
                ))}
                {section.bullets?.length > 0 && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={4}>
                    {section.bullets.map((item) => (
                      <Box
                        key={item}
                        borderLeft="3px solid"
                        borderColor={brand}
                        pl={3}
                        py={1}
                      >
                        <Text color={bodyStrong}>
                          {item}
                        </Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </Box>
            ))}

            {/* FAQ */}
            <Box as="section">
              <Heading
                as="h2"
                fontSize={{ base: "xl", md: "2xl" }}
                mb={4}
                color={headingColor}
              >
                Frequently Asked Questions
              </Heading>
              <Accordion allowToggle bg={cardBg} borderRadius="xl" border="1px solid" borderColor={borderColor}>
                {page.faqs.map((faq) => (
                  <AccordionItem key={faq.question} border="none">
                    <h3>
                      <AccordionButton py={4} px={5}>
                        <Box flex="1" textAlign="left" fontWeight="600">
                          {faq.question}
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                    </h3>
                    <AccordionPanel pb={4} px={5} color={muted}>
                      {faq.answer}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>

            {/* Bottom CTA */}
            <Box
              textAlign="center"
              bg={cardBg}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="xl"
              p={{ base: 6, md: 10 }}
            >
              <Heading as="h2" fontSize={{ base: "xl", md: "2xl" }} mb={3}>
                {page.ctaHeading}
              </Heading>
              <Text color={muted} mb={6} maxW="600px" mx="auto">
                {page.ctaText}
              </Text>
              <Stack direction={{ base: "column", sm: "row" }} spacing={4} justify="center">
                <Button
                  onClick={onOpen}
                  size="lg"
                  bg={brand}
                  color="white"
                  borderRadius="full"
                  _hover={{ bg: "#2A46B0" }}
                >
                  Request Demo
                </Button>
                <Button
                  onClick={() => router.push("/directTrialpage")}
                  size="lg"
                  variant="outline"
                  borderColor={brand}
                  color={brand}
                  borderRadius="full"
                  _hover={{ bg: brand, color: "white" }}
                >
                  Start Free Trial
                </Button>
              </Stack>
            </Box>
          </VStack>
        </Container>

        <Footer />
      </Box>

      <DemoSignupForm isOpen={isOpen} onClose={onClose} />
    </>
  );
}
